import { useState, useEffect, useRef, useCallback } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router-dom';

// Simple deep equal using JSON stringify (sufficient for form data)
const isEqual = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch (e) {
    return false;
  }
};

/**
 * Hook for AutoSave and Leave Protection
 * 
 * @param {Object} data - The form data to watch
 * @param {Function} onSave - Async function to save data
 * @param {boolean} isEditable - Whether saving is enabled
 * @param {number} debounceMs - Debounce time in ms (default 2000)
 * @param {boolean} blockNavigation - Whether to block navigation on unsaved changes
 */
export const useAutoSave = ({ 
  data, 
  onSave, 
  isEditable = true, 
  debounceMs = 2000,
  blockNavigation = true
}) => {
  const [lastSavedData, setLastSavedData] = useState(data); 
  const [status, setStatus] = useState('saved'); // 'saved', 'saving', 'modified', 'error'
  const [lastSavedTime, setLastSavedTime] = useState(null);
  const [savingError, setSavingError] = useState(null);
  
  const dataRef = useRef(data);
  const saveTimeoutRef = useRef(null);
  const initialLoadRef = useRef(true);

  // Initialize lastSavedData on first valid data (or when data structurally changes significantly? No, just trust parent)
  // Actually, we must be careful not to set "modified" immediately on mount if data depends on fetch.
  // The parent should only pass data once loaded.

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Handle initial data load synchronization
  useEffect(() => {
    if (initialLoadRef.current && data) {
      setLastSavedData(data);
      initialLoadRef.current = false;
    }
  }, [data]); // Only runs if data reference changes, but we want to sync initially.

  const isDirty = !isEqual(data, lastSavedData);

  // Core save function
  const executeSave = useCallback(async (currentData, isAuto = false) => {
    if (!isEditable) return; 
    
    setStatus('saving');
    setSavingError(null);
    try {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      
      await onSave(currentData);
      
      setLastSavedData(currentData);
      setLastSavedTime(new Date());
      setStatus('saved');
    } catch (error) {
      console.error("AutoSave failed", error);
      setStatus('error');
      setSavingError(error.message);
      
      if (isAuto) {
          // Retry logic could be added here
      } else {
        throw error; // Rethrow for manual saves
      }
    }
  }, [onSave, isEditable]);

  // Debounced auto-save effect
  useEffect(() => {
    if (!isEditable || initialLoadRef.current) return;
    
    // If data is same as last saved, we are saved.
    if (isEqual(data, lastSavedData)) {
        if (status === 'modified') setStatus('saved');
        return;
    }

    setStatus('modified');

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      executeSave(data, true);
    }, debounceMs);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [data, lastSavedData, isEditable, debounceMs, executeSave, status]);

  // Visibility change (Tab switch / Minimize)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && isDirty) {
         // Attempt to save immediately
         executeSave(dataRef.current, true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isDirty, executeSave]);

  // Before Unload (Browser close/refresh)
  useBeforeUnload(
    useCallback(
      (event) => {
        if (isDirty && blockNavigation) {
          event.preventDefault();
          // Standard way to show dialog in modern browsers
          event.returnValue = "You have unsaved changes. Are you sure you want to leave?"; 
          return "You have unsaved changes. Are you sure you want to leave?";
        }
      },
      [isDirty, blockNavigation]
    )
  );

  // Router Blocking using unstable_useBlocker (React Router v6.4+)
  // This hook returns a Blocker object { state: "unblocked" | "blocked" | "proceeding", proceed(), reset(), location }
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      blockNavigation && isEditable && isDirty && currentLocation.pathname !== nextLocation.pathname
  );
    
  return {
    status,        // 'saved', 'saving', 'modified', 'error'
    lastSavedTime, // Date object
    isDirty,       // boolean
    blocker,       // Blocker object for custom modal UI
    savingError,   // Error message if any
    manualSave: () => executeSave(dataRef.current, false)
  };
};
